import React, { FC, ReactElement, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { AxiosError, AxiosResponse } from 'axios'
import { Formik, FormikProps } from 'formik'

import { setLoaded } from '@store/modules/startup/actions'
import { factorAuthConfirm, signin } from '@store/modules/user/actions'
import { Nullable } from '@store/types/commonTypes'
import { ResponseCode, SigninFormMode } from '@const/consts'

import validationSchema from './validationSchema'

import { IEmailFormProps as IProps, IEmailFormState as IState, ISignInData } from './types'
import EmailInputWrapper from './components/EmailInputWrapper'
import CodeInput from '../CodeInput/CodeInput'

const initialValues: IState = {
  code: null,
  email: '',
  password: '',
  remember: false
}

const EmailForm: FC<IProps> = (props) => {
  const { isSending, history, location: { state }, onSetMode, onIsSendingChange, onServerErrorChange } = props
  const [isShowEmailCode, setIsShowEmailCode] = useState(false)
  const [tranId, setTranId] = useState<Nullable<string>>(null)

  const { t } = useTranslation()
  const dispatch = useDispatch<any>()

  useEffect(() => onServerErrorChange(''), [isShowEmailCode])

  const handleSubmit = ({ remember, code, ...data }: IState): void => {
    handleSignIn(remember, data, code)
  }

  const handleSignIn = (remember: boolean, data: ISignInData, code?: Nullable<number>): void => {
    onIsSendingChange(true)

    if (isShowEmailCode && tranId && code) {
      handleFactorAuthConfirm(remember, code)
    }
    else {
      dispatch(signin(data, remember))
        .then((resp: AxiosResponse) => {
          if (resp.status === ResponseCode.GET) {
            authorizeApp()
          }
          if (resp.status === 201) {
            onIsSendingChange(false)
            setIsShowEmailCode(true)
            setTranId(resp.data.tranId)
          }
        }, displayServerError)
    }
  }

  const handleFactorAuthConfirm = (remember: boolean, code: Nullable<number>): void => {
    dispatch(factorAuthConfirm({ tranId, code }, remember))
      .then((resp: AxiosResponse) => {
        if (resp.status === ResponseCode.POST) {
          authorizeApp()
        }
      }, displayServerError)
  }

  const handlePhoneLoginClick = (): void => {
    onSetMode(SigninFormMode.PHONE)
  }

  const handleSSOLoginClick = (): void => {
    onSetMode(SigninFormMode.SSO)
  }

  const authorizeApp = (): void => {
    dispatch(setLoaded(false))

    state?.from && history.push(state?.from)

    history.push("/")
  }

  const displayServerError = (err: AxiosError<any>): void => {
    onIsSendingChange(false)
    const errMessage = err.response?.data?.message
    onServerErrorChange(errMessage ?? t('notification:errorTitle'))
  }

  const renderForm = (props: FormikProps<IState>): ReactElement => {
    const { handleSubmit } = props

    return (
      <form onSubmit={handleSubmit} noValidate className='full-width'>
        {!isShowEmailCode
        ? <EmailInputWrapper
            {...props}
            isSending={isSending}
            onPhoneLoginClick={handlePhoneLoginClick}
            onServerErrorChange={onServerErrorChange}
            onSSOLoginClick={handleSSOLoginClick}
          />
        : <CodeInput
            {...props}
            isSending={isSending}
            onNavigationClick={() => setIsShowEmailCode(false)}
            onEmailSubmit={handleSignIn}
            onSSOLoginClick={handleSSOLoginClick}
            onServerErrorChange={onServerErrorChange}
          />
        }
      </form>
    )
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
      validateOnChange
      validateOnBlur={false}
      validationSchema={validationSchema}
    >
      {renderForm}
    </Formik>
  )
}

export default EmailForm
